import { apiFetch, type ApiTarget } from "./account-api.js";
/**
 * The one seam between this client's destinations and the server's domain API.
 *
 * Most of these routes do not exist yet: `domain-api.ts` serves requests and
 * runs, and nothing else. That is a fact the user should see stated plainly on
 * the page, not a reason to hide the destination — so "the server does not
 * offer this yet" is a first-class outcome here rather than an error, and every
 * caller has to handle it. It is kept distinct from "that object does not
 * exist", which the served routes do answer: `501` means unbuilt, a `404`
 * carrying `{ error: "not_found" }` means missing. When the routes land, only
 * this file changes.
 */

import { CLIENT_HEADERS } from "./generated/compatibility.js";

import type { AlertAction } from "./alert-intents.js";
import type { RoutableAlert } from "./destinations.js";
import type { JobVerdict, RequestState, RunState } from "./run-state.js";

export type Failure = "unavailable" | "not_found" | "unauthorized" | "offline" | "error" | "conflict" | "rejected";

export type Fetched<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly reason: Failure; readonly detail: string };

export interface DomainRequest {
  readonly requestId: string;
  readonly conversationId: string | null;
  readonly type: string;
  readonly state: RequestState;
}

export type LedgerRequestState = "pending" | "answered" | "consumed" | "expired" | "withdrawn" | "superseded";

/**
 * One choice the question offered.
 *
 * `id` is the server's normalized identifier and the only value an answer may
 * carry; `label` is what the person reads. Keeping both means a relabelled
 * choice does not change what a stored decision means.
 */
export interface LedgerChoice {
  readonly id: string;
  readonly label: string;
  readonly destructive: boolean;
}

/** The typed link from a follow-up back to the record that prompted it. */
export interface LedgerThreadParent {
  readonly kind: "request" | "notification";
  readonly id: string;
}

export interface LedgerRequest {
  readonly requestId: string;
  readonly direction: "to_owner" | "to_agent";
  readonly state: LedgerRequestState;
  readonly subjectRef: string;
  readonly reason: string;
  readonly body: string;
  readonly answer: string | null;
  readonly outcome: string | null;
  readonly createdAt: string;
  /** Present since the prompt-parity contract; absent on records stored before it. */
  readonly title?: string | null;
  readonly context?: Record<string, unknown> | null;
  readonly actions?: readonly LedgerChoice[];
  readonly allowsFreeform?: boolean;
  readonly threadKey?: string | null;
  readonly threadParent?: LedgerThreadParent | null;
  readonly collapseKey?: string | null;
  readonly answerAction?: string | null;
  readonly answeredAt?: string | null;
  readonly answeredBy?: string | null;
  readonly supersedesId?: string | null;
  readonly supersededBy?: string | null;
  readonly retiredReason?: string | null;
  readonly resolvedAt?: string | null;
  readonly expiresAt?: string | null;
  readonly deadlineAt?: string | null;
  readonly updatedAt?: string;
}

export type LedgerNotificationState = "published" | "superseded" | "withdrawn";

/** A notice published to the owner. It carries no question and takes no answer. */
export interface LedgerNotification {
  readonly notificationId: string;
  readonly title: string;
  readonly body: string;
  readonly context?: Record<string, unknown> | null;
  readonly subjectRef: string;
  readonly reason: string;
  readonly state: LedgerNotificationState;
  readonly threadKey?: string | null;
  readonly threadParent?: LedgerThreadParent | null;
  readonly supersedesId?: string | null;
  readonly supersededBy?: string | null;
  readonly createdAt: string;
}

/** What the owner is submitting: a chosen action, freeform text, or both. */
export interface LedgerAnswer {
  readonly answerAction?: string | null;
  readonly answer?: string | null;
}
export interface RaiseLedgerRequest { readonly body: string; readonly subjectRef: string; readonly reason: string; readonly originRef: string; readonly idempotencyKey: string; }

export interface DomainRun {
  readonly runId: string;
  readonly requestId: string;
  readonly attempt: number;
  readonly state: RunState;
  readonly waitReason: string | null;
  /**
   * Why a failed run failed, joined from the result it stored at failure time.
   * Null for every other state, and for a failed run that recorded no cause.
   */
  readonly failureDetail: string | null;
}

/** The counts a job reported at the end of its last run. All four, so a zero is a fact. */
export interface JobCounts {
  readonly listed: number;
  readonly resolved: number;
  readonly raised: number;
  readonly failed: number;
}

/** What the client reads from `GET /v1/platform/jobs/<name>`: a job's newest check-in. */
export interface JobCheckin {
  readonly job: string;
  readonly cadenceSeconds: number;
  readonly runs: number;
  readonly verdict: JobVerdict | null;
  readonly counts: JobCounts | null;
  readonly lastStartedAt: string | null;
  readonly lastFinishedAt: string | null;
  readonly lastSeenAt: string;
}

export interface AlertRecord extends RoutableAlert {
  readonly type: string;
  readonly title?: string;
  readonly state: string;
  readonly urgency: string;
  readonly createdAt: string;
  /**
   * How many times this problem has happened. One alert, not one per occurrence.
   *
   * Optional, like every other field the server grew after a client shipped: a
   * deployment that predates the collapsed inbox sends neither this nor
   * `lastOccurredAt`, and a row without them reads as a single occurrence, which
   * is exactly what it is.
   */
  readonly occurrences?: number;
  /** The singular noun for one occurrence, sent by the type that owns it. */
  readonly occurrenceLabel?: string;
  /** When the problem was last seen — not when the alert was last touched. */
  readonly lastOccurredAt?: string;
  readonly payload?: Record<string, unknown>;
}

/** What the user is told when a destination has no server behind it yet. */
export const UNAVAILABLE_DETAIL = "The hosted vault does not serve this yet. The destination is stable; the data is not there.";

/** What the user is told when the route exists and the object does not. */
export const NOT_FOUND_DETAIL = "The hosted vault has no such object. The address is well-formed; nothing is stored under it.";

function failure(reason: Failure, detail: string): Fetched<never> {
  return { ok: false, reason, detail };
}

function responseDetail(status: number, suffix = ""): string {
  return status === 426
    ? "This client and server use incompatible versions. Reload RecallVault for the latest client, or update the self-hosted server."
    : `The hosted vault returned ${status}.${suffix}`;
}

/**
 * Separates "this object does not exist" from "this route is not built yet".
 *
 * The server answers a genuinely missing object with `404` and an envelope of
 * `{ error: "not_found" }` (`server/src/domain-api.ts`, `server/src/errors.ts`).
 * Anything else answering `404` — a route the deployed server does not serve —
 * carries no such envelope. `501` is the contract for an unimplemented route,
 * so it is never read as a missing object.
 */
async function missing(response: Response): Promise<Fetched<never>> {
  if (response.status === 501) return failure("unavailable", UNAVAILABLE_DETAIL);
  try {
    const body = await response.json() as { error?: unknown };
    if (body !== null && typeof body === "object" && body.error === "not_found") {
      return failure("not_found", NOT_FOUND_DETAIL);
    }
  } catch {
    // A 404 without a readable envelope is a route this server does not serve.
  }
  return failure("unavailable", UNAVAILABLE_DETAIL);
}

async function get<T>(origin: ApiTarget, path: string): Promise<Fetched<T>> {
  let response: Response;
  try {
    response = await apiFetch(origin, `${path}`, { credentials: "include", headers: CLIENT_HEADERS });
  } catch {
    return failure("offline", "This browser could not reach the hosted vault.");
  }
  if (response.status === 401 || response.status === 403) {
    return failure("unauthorized", "Sign in again to read this from the hosted vault.");
  }
  if (response.status === 404 || response.status === 501) return missing(response);
  if (!response.ok) return failure("error", responseDetail(response.status));
  try {
    return { ok: true, value: await response.json() as T };
  } catch {
    return failure("error", "The hosted vault returned a response this client could not read.");
  }
}

export function fetchRequest(origin: ApiTarget, requestId: string): Promise<Fetched<{ request: DomainRequest }>> {
  return get(origin, `/v1/platform/requests/${encodeURIComponent(requestId)}`);
}

export function fetchLedgerRequest(origin: ApiTarget, requestId: string): Promise<Fetched<{ request: LedgerRequest }>> {
  return get(origin, `/v1/platform/ledger/requests/${encodeURIComponent(requestId)}`);
}

export async function raiseLedgerRequest(origin: ApiTarget, input: RaiseLedgerRequest): Promise<Fetched<{ request: LedgerRequest }>> {
  let response: Response;
  try {
    response = await apiFetch(origin, `/v1/platform/ledger/requests`, {
      method: "POST", credentials: "include", headers: { ...CLIENT_HEADERS, "content-type": "application/json", "idempotency-key": input.idempotencyKey },
      body: JSON.stringify({ direction: "to_agent", body: input.body, source: "recallvault-web-ask",
        origin_kind: "app", origin_ref: input.originRef, subject_ref: input.subjectRef, reason: input.reason }),
    });
  } catch { return failure("offline", "This browser could not reach the hosted vault. Copy the prompt to use it manually."); }
  if (response.status === 401 || response.status === 403) return failure("unauthorized", "Sign in again to submit this request.");
  if (!response.ok) return failure("error", responseDetail(response.status, " Copy the prompt to use it manually."));
  try { return { ok: true, value: await response.json() as { request: LedgerRequest } }; }
  catch { return failure("error", "The hosted vault stored an unreadable response. Copy the prompt to use it manually."); }
}

/**
 * Reads back the message the server refused with.
 *
 * A refused answer is the one case where the server's own words are the most
 * useful thing this client can show: "Request is already answered" and "This
 * question does not allow freeform answers" are both precise and both actionable,
 * and paraphrasing them here would only make them vaguer.
 */
async function refusal(response: Response, reason: Failure, fallback: string): Promise<Fetched<never>> {
  try {
    const body = await response.json() as { message?: unknown };
    if (typeof body?.message === "string" && body.message.trim().length > 0) return failure(reason, body.message);
  } catch {
    // A refusal without a readable envelope still has to say something.
  }
  return failure(reason, fallback);
}

/**
 * Submits the owner's decision about a question.
 *
 * The server is the authority on whether this question still takes an answer,
 * so a `409` is reported as its own outcome rather than a generic error: the
 * question was answered elsewhere, replaced, or retired while it was on screen,
 * and the honest response is to say so and re-read it — never to leave the page
 * showing a decision the ledger does not hold.
 */
export async function answerLedgerRequest(
  origin: ApiTarget,
  requestId: string,
  answer: LedgerAnswer,
): Promise<Fetched<{ request: LedgerRequest }>> {
  let response: Response;
  try {
    response = await apiFetch(origin, `/v1/platform/ledger/requests/${encodeURIComponent(requestId)}/actions`, {
      method: "POST",
      credentials: "include",
      headers: { ...CLIENT_HEADERS, "content-type": "application/json" },
      body: JSON.stringify({
        action: "answer",
        ...(answer.answerAction == null ? {} : { answer_action: answer.answerAction }),
        ...(answer.answer == null ? {} : { answer: answer.answer }),
      }),
    });
  } catch {
    return failure("offline", "This browser could not reach the hosted vault. Your answer was not recorded; try again.");
  }
  if (response.status === 401 || response.status === 403) return failure("unauthorized", "Sign in again to answer this question.");
  if (response.status === 409) return refusal(response, "conflict", "This question changed before your answer arrived.");
  if (response.status === 400) return refusal(response, "rejected", "The hosted vault refused this answer.");
  if (response.status === 404 || response.status === 501) return missing(response);
  if (!response.ok) return failure("error", responseDetail(response.status, " Your answer was not recorded; try again."));
  try {
    return { ok: true, value: await response.json() as { request: LedgerRequest } };
  } catch {
    return failure("error", "The hosted vault stored an unreadable response. Reload to see whether your answer landed.");
  }
}

/**
 * The largest page the server will serve (`MAX_PAGE_LIMIT` in
 * `request-service.ts`), asked for explicitly so a full read costs the fewest
 * round trips rather than defaulting to 50.
 */
export const LEDGER_PAGE_LIMIT = 200;

/**
 * How many pages a whole-history read will follow before it stops.
 *
 * A cap is not an admission that the history might be longer than this client
 * can show; it is a refusal to loop forever against a server that keeps
 * handing back a cursor. When it bites, `truncated` says so and the surface
 * showing the history has to say so too — a partial history that looks
 * complete is the failure this whole read exists to avoid.
 */
export const LEDGER_MAX_PAGES = 25;

/** A history read that followed every cursor it was given, or admits it did not. */
export interface LedgerHistory<T> {
  readonly records: readonly T[];
  /** True when the page cap stopped the read with a cursor still outstanding. */
  readonly truncated: boolean;
}

/** What one page of the request listing carries, counts included. */
export interface LedgerRequestPage {
  readonly requests: readonly LedgerRequest[];
  /** Non-null when more rows matched than this page carried. */
  readonly nextCursor: string | null;
  /** Tenant-wide, independent of this page's filters. */
  readonly pending: number;
  readonly answeredNotConsumed: number;
}

/** How an enumeration of questions is narrowed. Everything is optional. */
export interface LedgerRequestQuery {
  readonly direction?: "to_owner" | "to_agent";
  readonly states?: readonly LedgerRequestState[];
  readonly threadKey?: string;
  readonly cursor?: string | null;
  readonly limit?: number;
}

function listingPath(query: LedgerRequestQuery): string {
  const params = new URLSearchParams();
  if (query.direction !== undefined) params.set("direction", query.direction);
  if (query.threadKey !== undefined) params.set("thread_key", query.threadKey);
  if (query.limit !== undefined) params.set("limit", String(query.limit));
  if (query.cursor != null && query.cursor !== "") params.set("cursor", query.cursor);
  for (const state of query.states ?? []) params.append("state", state);
  return `/v1/platform/ledger/requests?${params.toString()}`;
}

/**
 * One page of questions, with the tenant-wide outstanding counts beside it.
 *
 * The counts are what an inbox is for: they are the server's answer to "how
 * much is owed", and they do not change when the caller filters the page.
 */
export function fetchLedgerRequests(origin: ApiTarget, query: LedgerRequestQuery = {}): Promise<Fetched<LedgerRequestPage>> {
  return get(origin, listingPath(query));
}

/** Every state a completed history repeats, so history is never the open queue. */
const EVERY_REQUEST_STATE: readonly LedgerRequestState[] =
  ["pending", "answered", "consumed", "expired", "withdrawn", "superseded"];

/**
 * Every question in one thread, terminal states included, so history is complete.
 *
 * Complete means every page: the listing is a keyset cursor, and reading only
 * the first page of a long thread would show a history that looks whole and is
 * not. Cursors are followed until the server stops handing them back, and if
 * the page cap stops the read first the result says `truncated` rather than
 * letting the caller present a partial thread as the whole one.
 */
export async function fetchLedgerThread(origin: ApiTarget, threadKey: string): Promise<Fetched<LedgerHistory<LedgerRequest>>> {
  const records: LedgerRequest[] = [];
  let cursor: string | null = null;
  for (let page = 0; page < LEDGER_MAX_PAGES; page += 1) {
    const result: Fetched<LedgerRequestPage> = await fetchLedgerRequests(origin, {
      threadKey, states: EVERY_REQUEST_STATE, limit: LEDGER_PAGE_LIMIT, cursor,
    });
    if (!result.ok) return result;
    records.push(...(result.value.requests ?? []));
    cursor = result.value.nextCursor ?? null;
    if (cursor == null || cursor === "") return { ok: true, value: { records, truncated: false } };
  }
  return { ok: true, value: { records, truncated: true } };
}

/** One notice, the object a `ledger-notification` alert opens. */
export function fetchLedgerNotification(origin: ApiTarget, notificationId: string): Promise<Fetched<{ notification: LedgerNotification }>> {
  return get(origin, `/v1/platform/ledger/notifications/${encodeURIComponent(notificationId)}`);
}

/**
 * Every notice in one thread, so a mixed thread can be assembled from both
 * histories. Paged exactly as the question history is, and for the same reason.
 */
export async function fetchLedgerNotifications(origin: ApiTarget, threadKey: string): Promise<Fetched<LedgerHistory<LedgerNotification>>> {
  const records: LedgerNotification[] = [];
  let cursor: string | null = null;
  for (let page = 0; page < LEDGER_MAX_PAGES; page += 1) {
    const params = new URLSearchParams({ thread_key: threadKey, limit: String(LEDGER_PAGE_LIMIT) });
    if (cursor != null && cursor !== "") params.set("cursor", cursor);
    const result: Fetched<{ notifications: readonly LedgerNotification[]; nextCursor: string | null }> =
      await get(origin, `/v1/platform/ledger/notifications?${params.toString()}`);
    if (!result.ok) return result;
    records.push(...(result.value.notifications ?? []));
    cursor = result.value.nextCursor ?? null;
    if (cursor == null || cursor === "") return { ok: true, value: { records, truncated: false } };
  }
  return { ok: true, value: { records, truncated: true } };
}

export function fetchRun(origin: ApiTarget, runId: string): Promise<Fetched<{ run: DomainRun }>> {
  return get(origin, `/v1/platform/runs/${encodeURIComponent(runId)}`);
}

export function fetchConversation(origin: ApiTarget, conversationId: string): Promise<Fetched<unknown>> {
  return get(origin, `/v1/platform/conversations/${encodeURIComponent(conversationId)}`);
}

export function fetchResult(origin: ApiTarget, resultId: string): Promise<Fetched<unknown>> {
  return get(origin, `/v1/platform/results/${encodeURIComponent(resultId)}`);
}

export function fetchArtifact(origin: ApiTarget, artifactId: string): Promise<Fetched<unknown>> {
  return get(origin, `/v1/platform/artifacts/${encodeURIComponent(artifactId)}`);
}

export function fetchJob(origin: ApiTarget, job: string): Promise<Fetched<{ job: JobCheckin }>> {
  return get(origin, `/v1/platform/jobs/${encodeURIComponent(job)}`);
}

export function fetchSources(origin: ApiTarget): Promise<Fetched<unknown>> {
  return get(origin, "/v1/platform/sources");
}

export function fetchAlerts(origin: ApiTarget, scope: "live" | "history"): Promise<Fetched<{ alerts: readonly AlertRecord[] }>> {
  return get(origin, `/v1/platform/alerts?state=${scope}`);
}

export function fetchCounts(origin: ApiTarget): Promise<Fetched<unknown>> {
  return get(origin, "/v1/platform/counts");
}

/**
 * Sends one queued decision. The client keeps the intent until this resolves
 * successfully, so a rejection here never loses the user's choice.
 */
export async function postAlertAction(
  origin: ApiTarget,
  alertId: string,
  action: AlertAction,
  snoozeSeconds?: number,
): Promise<Fetched<unknown>> {
  let response: Response;
  try {
    response = await apiFetch(origin, `/v1/platform/alerts/${encodeURIComponent(alertId)}/actions`, {
      method: "POST",
      credentials: "include",
      headers: { ...CLIENT_HEADERS, "content-type": "application/json" },
      body: JSON.stringify({ action, ...(snoozeSeconds === undefined ? {} : { snoozeSeconds }) }),
    });
  } catch {
    return failure("offline", "This browser could not reach the hosted vault.");
  }
  if (response.status === 401 || response.status === 403) return failure("unauthorized", "Sign in again to act on this alert.");
  if (response.status === 404 || response.status === 501) return missing(response);
  if (!response.ok) return failure("error", responseDetail(response.status));
  return { ok: true, value: undefined };
}

/** The Web Push subscription as the browser hands it over, keys included. */
export interface WebPushSubscription {
  readonly endpoint: string;
  readonly keys: { readonly p256dh: string; readonly auth: string };
}

/**
 * The server's VAPID public key, which a browser needs before it can subscribe.
 *
 * A deployment with Web Push unconfigured answers `404`, which reads here as
 * "unavailable" — the same outcome as a route the server does not serve yet.
 */
export function fetchPushPublicKey(origin: ApiTarget): Promise<Fetched<{ public_key: string }>> {
  return get(origin, "/v1/push/vapid-public-key");
}

/**
 * Registers this browser as somewhere the tenant gets notified.
 *
 * The shape is the one `server/src/push-targets.ts` parses for `webpush`: the
 * endpoint is the address and the p256dh/auth keys ride alongside, even though
 * the channel sends no payload today, so that the day it does nothing has to
 * be re-registered.
 */
export async function registerWebPush(origin: ApiTarget, subscription: WebPushSubscription, deviceId: string): Promise<Fetched<{ target_id: string }>> {
  let response: Response;
  try {
    response = await apiFetch(origin, `/v1/push/token`, {
      method: "POST",
      credentials: "include",
      headers: { ...CLIENT_HEADERS, "content-type": "application/json" },
      body: JSON.stringify({ channel: "webpush", endpoint: subscription.endpoint, keys: subscription.keys, device_id: deviceId }),
    });
  } catch {
    return failure("offline", "This browser could not reach the hosted vault.");
  }
  if (response.status === 401 || response.status === 403) return failure("unauthorized", "Sign in again to turn on notifications.");
  if (response.status === 404 || response.status === 501) return missing(response);
  if (!response.ok) return failure("error", responseDetail(response.status));
  try {
    return { ok: true, value: await response.json() as { target_id: string } };
  } catch {
    return failure("error", "The hosted vault returned a response this client could not read.");
  }
}

export interface AccountExtension {
  readonly id: string;
  readonly title: string;
  readonly version: string;
  readonly enabled: boolean;
  readonly configurable: boolean;
}

export async function fetchAccountExtensions(origin: ApiTarget): Promise<Fetched<{ extensions: readonly AccountExtension[] }>> {
  const result = await get<{ extensions?: unknown }>(origin, "/v1/platform/extensions");
  if (!result.ok) return result;
  const entries = result.value?.extensions;
  if (!Array.isArray(entries) || !entries.every((entry: unknown) => {
    if (typeof entry !== "object" || entry === null) return false;
    const value = entry as Record<string, unknown>;
    return typeof value.id === "string" && typeof value.title === "string" && typeof value.version === "string"
      && typeof value.enabled === "boolean" && typeof value.configurable === "boolean";
  })) return failure("error", "The hosted vault returned an unreadable extension list.");
  return { ok: true, value: { extensions: entries as AccountExtension[] } };
}

export async function setAccountExtension(origin: ApiTarget, id: string, enabled: boolean, changeId: string): Promise<Fetched<unknown>> {
  let response: Response;
  try {
    response = await apiFetch(origin, `/v1/platform/extensions/${encodeURIComponent(id)}`, {
      method: "PUT", credentials: "include",
      headers: { ...CLIENT_HEADERS, "content-type": "application/json", "idempotency-key": changeId },
      body: JSON.stringify({ enabled }),
    });
  } catch { return failure("offline", "This browser could not reach the hosted vault. Retry to confirm the change."); }
  if (response.status === 401 || response.status === 403) return failure("unauthorized", "Sign in again to change extensions.");
  if (!response.ok) return failure("error", responseDetail(response.status, " Retry to confirm the change."));
  return { ok: true, value: undefined };
}
